import React, { useEffect, useState, useCallback, useRef, useMemo, useContext } from "react";
import { secureGet, securePost, PostExcel } from "../../config/api";
import { formatDateTime, formatAmount } from "../../utils/format";
import { useTranslation } from "react-i18next";
import { Helmet } from "react-helmet-async";
import { PrimaryButton } from "../../components/PrimaryButton";
import Table from "../../components/Table";
import Filter from "../../components/Filter";
import PageScroller from "../../components/PageScroller";
import TableLoadingArea from "../../components/TableLoadingArea";
import SnackbarNotification from '../../components/SnackbarNotification';
import UserContext from "../../Shared/UserContext";
import { saveAs } from 'file-saver';

import exportIcon from "../../icons/export.svg";

const InfosFinanciers = () => {
  const [paymentData, setPaymentsData] = useState([]);
  const [currentPageData, setCurrentPageData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    clientId: null,
    commercialId: null,
    dateRange: {},
    echu: null,
    needle: "",
  });
  const [clients, setClients] = useState([]);
  const [commercials, setCommercials] = useState([]);
  const [currentPage, setCurrentPage] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [pageSize, setPageSize] = useState(30);
  const { t } = useTranslation();
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const topRef = useRef(null);
  const { state: { role } } = useContext(UserContext);
  const showSnackbar = (message, severity = 'success', manual = false) => {
    setSnackbar({ open: true, message, severity, manual });
  };
  const abortController = useMemo(() => new AbortController(), []);
  const { signal } = abortController;

  const columns = [
    { label: t(`columns.paymentNumber`), key: "deliveryNoteNumber" },
    {
      label: t(`columns.createdAt`), key: "createdAt",
      render: (date) => formatDateTime(date)
    },
    { label: "Code Client", key: "clientCode", render: (client, row) => row.client?.code },
    { label: "Client", key: "clientName", render: (client, row) => row.client?.name },
    { label: t(`columns.by`), key: "user", render: (user) => `${user.firstName} ${user.lastName}` },
    { label: t(`columns.montant`), key: "priceTTC" },
    {
      label: t(`columns.echeance`),
      render: (client, row) => {
        if (!row || !row.createdAt || !row.client) return null;

        const createdAt = new Date(row.createdAt);
        const allowedDelay = parseInt(
          row.client?.echeanceAutorisee ?? "0",
          10
        );

        const echeanceDate = new Date(createdAt);
        echeanceDate.setDate(echeanceDate.getDate() + allowedDelay);

        const today = new Date();
        const isEchu = echeanceDate < today;

        return (
          <div>
            {isEchu ? (
              <span className="text-white text-[12px] bg-[#06D6A0] font-semibold px-2 py-1 rounded-full  inline-block w-fit text-center">{t(`fields.payments.echu`)}</span>
            ) : (
              <span className="text-white text-[12px] bg-[#EF466F] font-semibold px-2 py-1 rounded-full  inline-block w-fit text-center">{t(`fields.payments.nonEchu`)}</span>
            )}
          </div>
        );
      }
    },
    {
      label: "Solde",
      key: "solde",
      render: (_, row) => formatAmount((row.priceTTC ?? 0) - (row.totalAttachedAmount ?? 0))
    }
  ];

  const filterOptions = [
    {
      name: "needle",
      type: "search",
    },
    {
      name: "dateRange",
      type: "daterange",
    },
    {
      name: "clientId",
      label: "Client",
      type: "dropdown",
      options: [...clients.map((client) => ({
        value: client.id,
        reference: client.code,
        name: client.name
      }))],
    },
    {
      name: "commercialId",
      label: t(`filters.orders.commercial`),
      type: "dropdown",
      options: [
        ...commercials.map((commercial) => ({
          value: commercial.id,
          label: `${commercial.firstName} ${commercial.lastName}`,
        })),
      ],
    },
    {
      name: "echu",
      label: t(`columns.echeance`),
      type: "dropdown",
      options: [
        { value: "true", label: t(`fields.payments.echu`) },
        { value: "false", label: t(`fields.payments.nonEchu`) },
      ],
    },
    {
      name: "trierPar",
      label: t(`filters.products.filterBy`),
      type: "dropdown",
      options: [
        { value: "BY_DATE_DESC", label: t("filters.sortByDateDesc") },
        { value: "BY_DATE_ASC", label: t("filters.sortByDateAsc") },
        { value: "BY_AMOUNT_DESC", label: t("filters.sortByAmountDesc") },
        { value: "BY_AMOUNT_ASC", label: t("filters.sortByAmountAsc") },
        { value: "BY_CLIENT_NAME", label: t("filters.sortByClientName") },
        { value: "BY_CLIENT_CODE", label: t("filters.sortByClientCode") },
        { value: "BY_COMMERCIAL", label: t("filters.sortByCommercial") },
        { value: "BY_NUMBER", label: t("filters.sortByNumber") },
      ],
    }
  ];

  useEffect(() => {
    const fetchDropdownOptions = async () => {
      try {
        const clientResponse = await secureGet("/clients", showSnackbar, { signal });
        setClients(clientResponse.data);

        const commercialResponse = await secureGet("/users/ROLE_COMMERCIAL", showSnackbar, { signal });
        setCommercials(commercialResponse.data);
      } catch (error) {
        if (error.name !== 'AbortError') {
          console.error("Error fetching dropdown options:", error);
        }
      }
    };
    fetchDropdownOptions();
    return () => {
      abortController.abort();
    };
  }, [abortController, signal]);

  const fetchPaginatedPayments = useCallback(async (page = 0, size = pageSize, activeFilters = filters) => {
    setLoading(true);
    const user = JSON.parse(localStorage.getItem("user") || "{}");
    const isCommercial = role === "ROLE_COMMERCIAL";
    try {
      const requestBody = {
        clientId: activeFilters.clientId || null,
        commercialId: isCommercial
          ? user?.id
          : activeFilters.commercialId || null,
        startDate: activeFilters.startDate || null,
        endDate: activeFilters.endDate || null,
        echu: activeFilters.echu || null,
        needle: activeFilters.needle || null,
        order: activeFilters.trierPar || null,
      };


      const response = await securePost(`/deliveryNotes/nonregle/paged?page=${page}&size=${size}`, requestBody, showSnackbar, {
        signal,
      });
      
      const { content, totalPages } = response.data;
      console.log(content)
      setPaymentsData(response.data.content);
      setCurrentPageData(content);
      setTotalPages(totalPages);
    } catch (error) {
      if (error.name !== 'AbortError') {
        console.error("Error fetching payments:", error);
      }
    } finally {
      setLoading(false);
    }
  }, [filters, pageSize, signal]);

  useEffect(() => {
    const abortController = new AbortController();
    fetchPaginatedPayments(0, pageSize);
    return () => {
      abortController.abort();
    };
  }, [pageSize, fetchPaginatedPayments]);

  const handlePageChange = (page) => {
    setCurrentPage(page);
    fetchPaginatedPayments(page, pageSize, filters);
    if (topRef.current) {
      topRef.current.scrollIntoView({ behavior: "smooth" });
    }
  };

  const handlePageSizeChange = (size) => {
    setPageSize(size);
    setCurrentPage(0);
    // fetchPaginatedPayments(0, size, filters);
  };

  const handleFilterChange = (filterName, filterValue) => {
    const updatedFilters = { ...filters };

    if (filterName === "dateRange") {
      const { startDate, endDate } = filterValue || {};
      updatedFilters.startDate = startDate || "";
      updatedFilters.endDate = endDate || "";
    } else if (filterName === "status") {
      updatedFilters.status = filterValue;
    } else {
      updatedFilters[filterName] = filterValue;
    }

    setFilters(updatedFilters);
    setCurrentPage(0);
    // fetchPaginatedPayments(0, pageSize, updatedFilters);
  };

  const resetFilters = () => {
    const defaultFilters = {
      clientId: null,
      commercialId: null,
      dateRange: {},
      echu: null,
      needle: "",
    };
    setFilters(defaultFilters);
    setCurrentPage(0);
    // fetchPaginatedPayments(0, pageSize, defaultFilters);
  };

  const handleExport = async () => {
    const payload = {
      clientId: filters.clientId !== "ALL" ? filters.clientId : null,
      commercialId: filters.commercialId || null,
      startDate: filters.startDate || null,
      endDate: filters.endDate || null,
      needle: filters.needle || null,
      echu: filters.echu || null,
    };

    try {
      const response = await PostExcel('/deliveryNotes/exportNonregle', payload, showSnackbar);
      const today = new Date();
      const formattedDate = today.toISOString().split('T')[0];
      const fileName = `infos_financieres_${formattedDate}.xlsx`;
      const blob = new Blob([response.data], {
        type: 'application/vnd.ms-excel',
      });

      saveAs(blob, fileName);
    } catch (error) {
      console.error("Export failed:", error);
    }
  };

  return (
    <div ref={topRef}>
      <Helmet>
        <title>GrayFox - {t("titles.infosFinancieres")}</title>
      </Helmet>
      <div className="flex items-center mb-[15px] ">
        <h1 className="text-[40px] font-semibold">{t(`titles.infosFinancieres`)}</h1>
        <div className="ml-auto text-[16px]">
          <PrimaryButton
            label={t(`actions.export`)}
            icon={exportIcon}
            onClick={() => handleExport()}
          />
        </div>
      </div>

      <Filter
        filters={filterOptions}
        onFilterChange={(filterName, filterValue) => handleFilterChange(filterName, filterValue)}
        resetFilters={resetFilters}
        filterValues={filters}
      />
      <TableLoadingArea loading={loading}>
        <Table
          columns={columns}
          data={currentPageData}
        />
      </TableLoadingArea>

      <PageScroller
        pageNumber={currentPage}
        totalPages={totalPages}
        pageSize={pageSize}
        onPageChange={handlePageChange}
        onPageSizeChange={handlePageSizeChange}
      />

      <SnackbarNotification
        open={snackbar.open}
        message={snackbar.message}
        severity={snackbar.severity}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        manual={snackbar.manual}
      />
    </div>
  );
};

export default InfosFinanciers;
